import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Client from '../models/serviceModels/Client';
import { getClientById, getEthnicities, getGenders, getRaces, mergeClients } from '../services/clientsService';
import Button from '../components/UI/Button';
import Loader from '../components/UI/Loader';
import ApiErrorMessages from '../components/UI/ApiErrorMessages';
import './ParticipantMergeCompare.css';
import Checkbox from '../components/UI/Checkbox';
import { formatJSONDateWithoutTime } from '../utils/dateHelpers';
import TruncatedText from '../components/UI/TruncatedText';
import { format } from 'date-fns';
import IconButton from '../components/UI/IconButton';
import { ACTIVITY_DATE_ID, BREAK_LINE_ID, CHECKBOX_GROUP_ID, CHECKBOX_ID, CLIENT_ID, DATE_ID, HEADER_ID, NOTES_ID, RADIO_BUTTON_ID, SELECT_LIST_ID } from '../constants/fieldTypes';
import { getDownloadUrl } from '../services/fileService';
import Gender from '../models/serviceModels/Gender';
import Race from '../models/serviceModels/Race';
import Ethnicity from '../models/serviceModels/Ethnicity';
import HouseholdMember from '../models/serviceModels/HouseholdMember';
import ClientCasePlan from '../models/serviceModels/ClientCasePlan';
import FormInstance from '../models/serviceModels/FormInstance';
import ProgramHistory from '../models/serviceModels/ProgramHistory';
import { ClientNote } from '../models/serviceModels/ClientNote';
import { UploadedFile } from '../models/pageModels/UploadedFile';
import Form from '../models/serviceModels/Form';
import { getClientForm } from '../services/formsService';
import FormField from '../models/serviceModels/FormField';
import ErrorMessages from '../components/UI/ErrorMessages';
import BackButton from '../components/UI/BackButton';
import Modal from '../components/UI/Modal';
import toast from "react-hot-toast";
import getclassNames from '../utils/getClassNames';
import { ClientActivatedLog } from '../models/serviceModels/ClientActivatedLog';
import { scrollLeft, scrollRight } from '../utils/elementHelpers';
import { optionalDateOfBirthEnabled } from '../utils/features';
import { OrganizationContext } from '../App';
import { getEthnicityFieldVisible, isDemographicVisible } from '../utils/demographicsSchema';

const ParticipantMergeCompare: React.FC = () => {
  const { mergeId = '' } = useParams();
  const navigate = useNavigate();
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>();
  const [participants, setParticipants] = useState<Client[]>([]);
  const [additionalInformationForm, setAdditionalInformationForm] = useState<Form>();
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<any>();
  const [modalErrors, setModalErrors] = useState<any>();
  const [openingFiles, setOpeningFiles] = useState<string[]>([]);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [races, setRaces] = useState<Race[]>([]);
  const [ethnicities, setEthnicities] = useState<Ethnicity[]>([]);
  const [expandedNestedOptions, setExpandedNestedOptions] = useState<string[]>([]);
  const [expandedNestedMergedOptions, setExpandedNestedMergedOptions] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<{ clientId: string, fields: string[] }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const organization = useContext(OrganizationContext);
  const showPronouns = isDemographicVisible(organization.value, 'pronouns');
  const showPhone = isDemographicVisible(organization.value, 'phone');
  const showEmail = isDemographicVisible(organization.value, 'email');
  const showGender = isDemographicVisible(organization.value, 'gender');
  const showRace = isDemographicVisible(organization.value, 'race');
  const showEthnicity = getEthnicityFieldVisible(organization.value);
  const showAddress = isDemographicVisible(organization.value, 'address');
  const showCity = isDemographicVisible(organization.value, 'city');
  const showState = isDemographicVisible(organization.value, 'state');
  const showZip = isDemographicVisible(organization.value, 'zip');

  useEffect(() => {
    if (mergeId) {
      const stored = localStorage.getItem(`mergeParticipants_${mergeId}`);
      if (stored) {
        const mergeSelection = JSON.parse(stored) as { selectedClientId?: string, additionalClientIds: string[] };
        if (mergeSelection?.additionalClientIds && mergeSelection.additionalClientIds.length !== 0) {
          const ids = [];
          if (mergeSelection.selectedClientId) {
            ids.push(mergeSelection.selectedClientId);
            setSelectedClientId(mergeSelection.selectedClientId);
          }

          if (mergeSelection.additionalClientIds && mergeSelection.additionalClientIds.length !== 0) {
            for (const id of mergeSelection.additionalClientIds) {
              if (!ids.includes(id)) {
                ids.push(id);
              }
            }
          }
          setParticipantIds(ids);
        } else {
          navigate('/participants/merge/search');
        }
      } else {
        navigate('/participants/merge/search');
      }
    } else {
      navigate('/participants/merge/search');
    }
  }, [navigate]);

  useEffect(() => {
    if (participantIds.length > 1) {
      setLoading(true);
      Promise.all([getGenders(), getEthnicities(), getRaces(), getClientForm(), ...participantIds.map(id => getClientById({
        id,
        includeCasePlans: true,
        includeFiles: true,
        includeForms: true,
        includeNotes: true,
        includePrograms: true,
        includeAdditionalInformation: true,
        includeActivatedLog: true
      }))])
        .then((results) => {
          const [gr, er, rr, af, ...pr] = results;
          setGenders(gr);
          setEthnicities(er);
          setRaces(rr);
          setAdditionalInformationForm(af);

          for (const p of pr) {
            if (!p.programs) continue;
            for (const pro of p.programs) {
              (pro as any).id = pro.programEnrollmentId;
            }
          }

          setParticipants(pr);
          setSelectedFields(pr.map(r => ({ clientId: r.id, fields: [] })));
        })
        .catch(e => {
          console.error(e);
          toast.error("Failed to load participants");
          setApiError(e);
        })
        .finally(() => {
          setLoading(false)
        });
    }
  }, [participantIds]);

  const handleOnToggleExpandedNestedMergedOption = (fieldName: string) => {
    if (expandedNestedMergedOptions.includes(fieldName)) {
      setExpandedNestedMergedOptions([
        ...expandedNestedMergedOptions.filter(o => o !== fieldName)
      ]);
    } else {
      setExpandedNestedMergedOptions([
        ...expandedNestedMergedOptions,
        fieldName
      ]);
    }
  };

  const handleOnToggleExpandedNestedOption = (fieldName: string) => {
    if (expandedNestedOptions.includes(fieldName)) {
      setExpandedNestedOptions([
        ...expandedNestedOptions.filter(o => o !== fieldName)
      ]);
    } else {
      setExpandedNestedOptions([
        ...expandedNestedOptions,
        fieldName
      ]);
    }
  };

  const validateMergedFields = () => {
    // clear errors initially
    setErrors([]);

    const newErrors = [];

    if (selectedFields.length === 0 || selectedFields.every(f => f.fields.length === 0)) {
      newErrors.push('Fields must be selected in order to merge');
    } else {
      let firstNameSelected = false;
      let lastNameSelected = false;
      let dateOfBirthSelected = false;
      let activatedLogsSelected = false;

      const requiredAdditionalFieldsSelected: { [key: string]: boolean } = {};
      const currentlyEnrolledProgramsSelected: ProgramHistory[] = [];
      const currentlyActivateLogs: ClientActivatedLog[] = [];

      if (additionalInformationForm?.fields) {
        for (const additionalInfofield of additionalInformationForm.fields) {
          if (additionalInfofield.required) {
            requiredAdditionalFieldsSelected[`additionalInformation_${additionalInfofield.name}`] = false;
          }
        }
      }

      for (const clientFields of selectedFields) {
        const matchingClient = participants.find(p => p.id === clientFields.clientId);
        if (clientFields.fields.includes("firstName")) {
          firstNameSelected = true;
        }

        if (clientFields.fields.includes("lastName")) {
          lastNameSelected = true;
        }

        if (clientFields.fields.includes("dateOfBirth")) {
          dateOfBirthSelected = true;
        }

        for (const k in requiredAdditionalFieldsSelected) {
          if (clientFields.fields.includes(k)) {
            requiredAdditionalFieldsSelected[k] = true;
          }
        }

        const activatedLogFields = clientFields.fields.filter(f => f.startsWith('activatedLog_'));
        if (activatedLogFields.length !== 0) {
          activatedLogsSelected = true;
        }
        for (const [_, logId] of activatedLogFields.map(alf => alf.split("_"))) {
          const matchingLog = matchingClient?.activatedLog?.find(al => al.id === logId);
          if (!matchingLog) {
            continue;
          }

          if (!matchingLog.deactivated) {
            currentlyActivateLogs.push(matchingLog);
          }
        }

        const programFields = clientFields.fields.filter(f => f.startsWith("programs_"));
        for (const [_, programEnrollmentId] of programFields.map(pf => pf.split("_"))) {
          const matchingEnrollment = matchingClient?.programs?.find(p => p.programEnrollmentId === programEnrollmentId);
          if (!matchingEnrollment) {
            continue;
          }

          if (!matchingEnrollment.programStop) {
            currentlyEnrolledProgramsSelected.push(matchingEnrollment);
          }
        }
      }

      if (!firstNameSelected) {
        newErrors.push('First Name selection is required');
      }

      if (!lastNameSelected) {
        newErrors.push('Last Name selection is required')
      }

      if (!optionalDateOfBirthEnabled && !dateOfBirthSelected) {
        newErrors.push('Birth Date selection is required')
      }

      if (!activatedLogsSelected) {
        newErrors.push('Activated Log - Atleast 1 log must be selected to merge');
      }

      if (currentlyActivateLogs.length > 1) {
        newErrors.push('Activated Log - Only 1 Currently Active log can be selected to merge');
      }

      if (additionalInformationForm) {
        for (const k in requiredAdditionalFieldsSelected) {
          if (!requiredAdditionalFieldsSelected[k]) {
            const matchingField = additionalInformationForm.fields.find(f => f.name === k.split('_')[1]);
            if (!matchingField) {
              console.warn('Matching field not found', k);
              continue;
            }

            newErrors.push(`Additional Information - ${matchingField.label} selection is required`);
          }
        }
      }

      if (currentlyEnrolledProgramsSelected.length !== 0) {
        const foundDuplicateEnrollments: { [key: string]: string } = {};
        for (const programHistory of currentlyEnrolledProgramsSelected) {
          const matchingProgram = currentlyEnrolledProgramsSelected.find(s => s.programId === programHistory.programId && s.programEnrollmentId !== programHistory.programEnrollmentId);
          if (matchingProgram) {
            foundDuplicateEnrollments[matchingProgram.programId] = matchingProgram.programName;
          }
        }
        const programIds = Object.keys(foundDuplicateEnrollments);
        if (programIds.length !== 0) {
          for (const pid of programIds) {
            newErrors.push(`Program Enrollment - Can only be Currently Enrolled in ${foundDuplicateEnrollments[pid]} once`);
          }
        }
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  }

  const handleOnMerge = () => {
    setModalErrors(undefined);

    if (!validateMergedFields()) {
      return;
    }

    setSubmitting(true);

    mergeClients({
      clients: [
        ...selectedFields
      ]
    }).then(r => {
      localStorage.removeItem(`mergeParticipants_${mergeId}`);
      toast.success("Successfully merged participants");
      navigate(`/participants/${r.clientId}/profile-demographics`);
    }).catch(e => {
      console.error(e);
      toast.error("Failed to merge participants");
      setModalErrors(e);
    }).finally(() => {
      setSubmitting(false);
    });
  };

  const getSelectedFormFieldValue = (formField: FormField) => {
    if (!additionalInformationForm?.fields) {
      return [{ value: undefined, found: false }];
    }

    const matchedValues = []
    for (const clientFields of selectedFields) {
      if (clientFields.fields.includes(`additionalInformation_${formField.name}`)) {
        const matchingClient = participants.find(p => p.id === clientFields.clientId);
        if (!matchingClient) {
          matchedValues.push({ value: undefined, found: true });
          continue;
        }

        if (!matchingClient.additionalInformation) {
          matchedValues.push({ value: undefined, found: true });
          continue;
        }

        const matchingFieldInstance = matchingClient.additionalInformation.instanceFields.find(f => f.name === formField.name);
        if (!matchingFieldInstance?.value) {
          matchedValues.push({ value: undefined, found: true });
          continue;
        }

        if (matchingFieldInstance.fieldTypeId === DATE_ID ||
          matchingFieldInstance.fieldTypeId === ACTIVITY_DATE_ID) {
          matchedValues.push({ value: formatJSONDateWithoutTime(matchingFieldInstance.value, 'MM/dd/yyyy'), found: true });
          continue;
        }

        if (matchingFieldInstance.fieldTypeId === RADIO_BUTTON_ID) {
          const parsedValue = JSON.parse(matchingFieldInstance.value);
          matchedValues.push({ value: parsedValue.label, found: true });
          continue;
        }

        if (matchingFieldInstance.fieldTypeId === CHECKBOX_ID) {
          matchedValues.push({ value: matchingFieldInstance.value === 'true' ? 'Yes' : 'No', found: true });
          continue;
        }

        if (matchingFieldInstance.fieldTypeId === CHECKBOX_GROUP_ID) {
          const parsedValue = JSON.parse(matchingFieldInstance.value);
          matchedValues.push({ value: parsedValue.map((v: any) => v.label).join(","), found: true });
          continue;
        }

        if (matchingFieldInstance.fieldTypeId === SELECT_LIST_ID) {
          const parsedValue = JSON.parse(matchingFieldInstance.value);
          matchedValues.push({ value: parsedValue.label, found: true });
          continue;
        }


        if (matchingFieldInstance.fieldTypeId === NOTES_ID) {
          const totalFiles = matchingFieldInstance.files ? matchingFieldInstance.files.length : 0;
          matchedValues.push({ value: `${matchingFieldInstance.value} (${totalFiles} files)`, found: true });
          continue;
        }

        matchedValues.push({ value: matchingFieldInstance?.value, found: true });
      }
    }

    if (matchedValues.length === 0) {
      matchedValues.push({ value: undefined, found: false });
    }

    return matchedValues;
  }


  const getSelectedFieldValue = (fieldName: string, propertyName: string) => {
    for (const clientFields of selectedFields) {
      if (clientFields.fields.includes(fieldName)) {
        const matchingClient = participants.find(p => p.id === clientFields.clientId);
        if (!matchingClient) {
          return { value: undefined, found: true };
        }

        return { value: (matchingClient as any)[propertyName] as any, found: true };
      }
    }

    return { value: undefined, found: false };
  }

  const getMergedDateOption = (label: string, fieldName: string, propertyName: string, required = false) => {
    const { value, found } = getSelectedFieldValue(fieldName, propertyName);
    return <div className={getclassNames("merged-option", found ? "highlight" : "")}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column">
          <div className="row align-items-center">
            <div className="column-large-6 margin-vertical-3">
              <label>{label}</label>
              {required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
            </div>
            <div className="column-large-6 margin-vertical-3">
              {value && formatJSONDateWithoutTime(value, 'MM/dd/yyyy')}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getMergedFieldOption = (formField: FormField) => {
    const matchingValues = getSelectedFormFieldValue(formField);
    const found = matchingValues.some(v => v.found);
    const values = matchingValues.map(v => v.value)
    return <div className={getclassNames("merged-option", found ? 'highlight' : '')}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column">
          <div className="row align-items-center">
            <div className="column-large-6 margin-vertical-3">
              <label>{formField.label}</label>
              {formField.required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
            </div>
            <div className="column-large-6 margin-vertical-3 text-overlap-wrap maw-200">
              {values.map((v, i) => {
                return <Fragment key={i}>
                  <p>{v}</p>
                </Fragment>
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getMergedOption = (label: string, fieldName: string, propertyName: string, required = false) => {
    const { value, found } = getSelectedFieldValue(fieldName, propertyName);
    return <div className={getclassNames("merged-option", found ? 'highlight' : '')}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column">
          <div className="row align-items-center">
            <div className="column-large-6 margin-vertical-3">
              <label>{label}</label>
              {required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
            </div>
            <div className="column-large-6 margin-vertical-3 text-overlap-wrap maw-200">
              {value}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getMergedRadioButtonOption = (label: string, fieldName: string, propertyName: string, options: { label: string; value: string }[]) => {
    const { value, found } = getSelectedFieldValue(fieldName, propertyName);
    const fieldValueName = options.find(o => o.value === value?.toString())?.label;
    return <div className={getclassNames("merged-option", found ? 'highlight' : '')}>

      <div className="row padding-vertical-1 align-items-end">
        <div className="column">
          <div className="row align-items-center">
            <div className="column-large-6 margin-vertical-3">
              <label>{label}</label>
            </div>
            <div className="column-large-6 margin-vertical-3 text-overlap-wrap maw-200">
              {fieldValueName}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getMergedCheckboxGroupOption = (label: string, fieldName: string, propertyName: string) => {
    const { value, found } = getSelectedFieldValue(fieldName, propertyName) as { value: { id: number, name: string }[], found: boolean };
    const fieldValueName = value?.map(fv => (fv.name))?.join(", ");
    return <div className={getclassNames("merged-option", found ? 'highlight' : '')}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column">
          <div className="row align-items-end">
            <div className="column-large-6 margin-vertical-3">
              <label>{label}</label>
            </div>
            <div className="column-large-6 margin-vertical-3 text-overlap-wrap maw-200">
              {fieldValueName}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getMergedSelectOption = (label: string, fieldName: string, propertyName: string, options: { label: string; value: string }[]) => {
    const { value, found } = getSelectedFieldValue(fieldName, propertyName);
    const fieldValueName = options.find(o => o.value === value?.toString())?.label;
    return <div className={getclassNames("merged-option", found ? 'highlight' : '')}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column">
          <div className="row align-items-end">
            <div className="column-large-6 margin-vertical-3">
              <label>{label}</label>
            </div>
            <div className="column-large-6 margin-vertical-3 text-overlap-wrap maw-200">
              {fieldValueName}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  const getOption = (fieldName: string, clientId: string, label: string, onlyOneSelected: boolean, value?: string) => {
    return <div className="row padding-vertical-1 align-items-end">
      <div className="column-5">
        <label>{label}</label>
      </div>
      <div className="column-6 text-overlap-wrap maw-200">
        {value || ''}
      </div>
      <div className="column-1">
        <Checkbox id={`select-checkbox-${clientId}-${fieldName}`}
          containerClassName="margin-top-0 unboxed padding-0"
          onChange={(checked) => handleOnSelected(clientId, fieldName, checked, onlyOneSelected)}
          checked={isFieldSelected(clientId, fieldName)} />
      </div>
    </div>;
  };

  const onSelected = (clientId: string, field: string, selected: boolean, onlyOneSelected: boolean) => {
    let existingClient = selectedFields.find(s => s.clientId === clientId);
    if (!existingClient) {
      existingClient = {
        clientId,
        fields: []
      };
      selectedFields.push(existingClient);
    }

    if (selected) {
      if (!existingClient.fields.includes(field)) {
        existingClient.fields.push(field);
      }
    } else {
      existingClient.fields = [
        ...existingClient.fields.filter(f => f !== field)
      ];
    }

    if (onlyOneSelected && selected) {
      for (const clientFields of selectedFields) {
        if (clientFields === existingClient) {
          continue;
        }

        if (clientFields.fields.includes(field)) {
          clientFields.fields = clientFields.fields.filter(f => f !== field);
        }
      }
    }

    return selectedFields;
  }

  const handleOnNestedOptionSelected = (clientId: string, field: string, selected: boolean, onlyOneSelected: boolean, totalPossibleOptions: number) => {
    if (totalPossibleOptions === 0) {
      return;
    }

    const modifiedFields = onSelected(clientId, field, selected, onlyOneSelected);

    const existingClient = modifiedFields.find(f => f.clientId === clientId);
    if (!existingClient) {
      console.warn(`Client Id ${clientId} not found`);
      return;
    }

    const parentFieldName = field.split('_')[0];

    if (selected) {
      if (existingClient.fields.filter(f => f.startsWith(`${parentFieldName}_`)).length === totalPossibleOptions &&
        !existingClient.fields.includes(parentFieldName)) {
        existingClient.fields = [
          ...existingClient.fields,
          parentFieldName
        ];
      }
    } else {
      if (existingClient.fields.includes(parentFieldName)) {
        existingClient.fields = existingClient.fields.filter(f => f !== parentFieldName);
      }
    }

    setSelectedFields([
      ...modifiedFields
    ]);
  }

  const handleOnParentOptionSelected = (clientId: string, field: string, selected: boolean, onlyOneSelected: boolean, subOptionFields: string[]) => {
    if (subOptionFields.length === 0) {
      return;
    }

    const modifiedFields = onSelected(clientId, field, selected, onlyOneSelected);

    const existingClient = modifiedFields.find(f => f.clientId === clientId);
    if (!existingClient) {
      console.warn(`Client Id ${clientId} not found`);
      return;
    }

    if (selected) {
      const newFields = [];
      for (const subOptionField of subOptionFields) {
        if (!existingClient.fields.includes(subOptionField)) {
          newFields.push(subOptionField);
        }
      }
      if (newFields.length !== 0) {
        existingClient.fields = [
          ...existingClient.fields,
          ...newFields
        ];
      }
    } else {
      existingClient.fields = existingClient.fields.filter(f => !subOptionFields.includes(f));
    }

    setSelectedFields([
      ...modifiedFields
    ]);
  };

  const handleOnSelected = (clientId: string, field: string, selected: boolean, onlyOneSelected: boolean) => {
    const modifiedFields = onSelected(clientId, field, selected, onlyOneSelected);

    setSelectedFields([
      ...modifiedFields
    ]);
  };

  const isFieldSelected = (clientId: string, field: string) => {
    console.log('selectedFields', selectedFields);
    const existingClient = selectedFields.find(f => f.clientId === clientId);
    let result = false;
    if (existingClient) {
      result = existingClient.fields.includes(field);
    }

    return result;
  };

  const handleOnFileOpen = (fileId: string) => {
    if (openingFiles.includes(fileId)) {
      return;
    }

    setOpeningFiles([
      ...openingFiles,
      fileId
    ]);

    getDownloadUrl({
      fileId: fileId
    }).then(r => {
      if (r.url) {
        const anchor = document.createElement('a');
        anchor.href = r.url;
        anchor.setAttribute('download', 'download');
        anchor.click();
      }
    }).catch(e => {
      console.error(e);
      toast.error("Failed to obtain download URL");
      setApiError(e);
    }).finally(() => {
      setOpeningFiles([
        ...openingFiles.filter(o => o !== fileId)
      ]);
    })
  }

  const getMergedParentOption = (label: string, fieldName: string, propertyName: string, subContentBuilder: (i: number, o: any) => any, required = false) => {
    const selectedSubOptions: any[] = [];

    for (const p of participants) {
      const subContent = (p as any)[propertyName] as any[];

      for (const clientFields of selectedFields) {
        const fields = clientFields.fields.filter(f => f.startsWith(`${fieldName}_`));
        for (const selectedField of fields) {
          const subOptionId = selectedField.split('_')[1];
          const matchingSubOption = subContent.find(c => c.id === subOptionId);
          if (matchingSubOption) {
            selectedSubOptions.push(matchingSubOption);
          }
        }
      }
    }

    return <div className={getclassNames("merged-option", selectedSubOptions.length !== 0 ? "highlight" : "")}>
      <div className="row padding-vertical-1 align-items-center">
        <div className="column-1 margin-bottom-3">
          <IconButton className="small flat"
            id={`expand-nested-options-${fieldName}`}
            icon={expandedNestedMergedOptions.includes(fieldName) ?
              <i className="ri-arrow-up-s-line"></i> :
              <i className="ri-arrow-down-s-line"></i>
            }
            onClick={() => handleOnToggleExpandedNestedMergedOption(fieldName)} />
        </div>
        <div className="column-11 margin-bottom-3">
          <label>{label} ({selectedSubOptions.length})</label>
          {required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
        </div>
      </div>
      {expandedNestedMergedOptions.includes(fieldName) && <>
        {selectedSubOptions.length === 0 && <div className="margin-top-3">No {label} selected</div>}
        {selectedSubOptions.length !== 0 && selectedSubOptions.map((o, i) => {
          return <div key={o.id} className="border margin-vertical-3 padding-3">
            {subContentBuilder(i, o)}
          </div>
        })}
      </>}
    </div>
  }

  const getMergedParentField = (label: string, fieldName: string, onlyOneSelected: boolean, propertyName: string, required: boolean, subContentBuilder: (index: number, source: any) => any) => {
    const selectedSubOptions: any[] = [];

    return <div className="merge-option-row">
      {participants.map(p => {
        const subContent = (p as any)[propertyName] as any[];
        const subOptionFields = subContent.map((f: any) => `${fieldName}_${f.id}`);

        for (const clientFields of selectedFields) {
          const fields = clientFields.fields.filter(f => f.startsWith(`${fieldName}_`));
          for (const selectedField of fields) {
            const subOptionId = selectedField.split('_')[1];
            const matchingSubOption = subContent.find(c => c.id === subOptionId);
            if (matchingSubOption) {
              selectedSubOptions.push(matchingSubOption);
            }
          }
        }

        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          <div className="row padding-vertical-1 align-items-center">
            <div className="column-1">
              <IconButton className="small flat"
                id={`expand-nested-options-${fieldName}`}
                icon={expandedNestedOptions.includes(fieldName) ?
                  <i className="ri-arrow-up-s-line"></i> :
                  <i className="ri-arrow-down-s-line"></i>
                }
                onClick={() => handleOnToggleExpandedNestedOption(fieldName)} />
            </div>
            <div className="column-10">
              <label>{label} ({subOptionFields.length})</label>
              {required && <span className="margn-start-1 text-danger hide-on-print">*</span>}
            </div>
            <div className="column-1">
              <Checkbox id={`select-checkbox-${p.id}-${fieldName}`}
                containerClassName="margin-top-0 unboxed padding-0"
                onChange={(checked) => handleOnParentOptionSelected(p.id, fieldName, checked, onlyOneSelected, subOptionFields)}
                checked={isFieldSelected(p.id, fieldName)} />
            </div>
          </div>
          {expandedNestedOptions.includes(fieldName) && <>
            {subContent.length !== 0 && subContent.map((c: any, i: number) => {
              return <div key={c.id} className={getclassNames("border margin-vertical-3 padding-3", isFieldSelected(p.id, `${fieldName}_${c.id}`) ? "highlight" : "")}>
                <div className="row padding-vertical-5 align-items-center">
                  <div className="column-11">
                    {subContentBuilder(i, c)}
                  </div>
                  <div className="column-1">
                    <Checkbox id={`select-checkbox-${p.id}-${c.id}-${fieldName}`}
                      containerClassName="margin-top-0 unboxed padding-0"
                      onChange={(checked) => handleOnNestedOptionSelected(p.id, `${fieldName}_${c.id}`, checked, onlyOneSelected, subOptionFields.length)}
                      checked={isFieldSelected(p.id, `${fieldName}_${c.id}`)} />
                  </div>
                </div>
              </div>
            })}
            {subContent.length === 0 && <>No {label} to merge</>}
          </>}
        </div>
      })}
    </div>
  }

  const getMergeFormField = (formField: FormField) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        const matchingField = p.additionalInformation?.instanceFields?.find(f => f.name === formField.name);

        let fieldValue = matchingField?.value;
        let subFieldValue = undefined;
        if (matchingField?.value && fieldValue) {
          if (matchingField.fieldTypeId === DATE_ID ||
            matchingField.fieldTypeId === ACTIVITY_DATE_ID) {
            fieldValue = formatJSONDateWithoutTime(fieldValue, 'MM/dd/yyyy');
          }

          if (matchingField.fieldTypeId === RADIO_BUTTON_ID) {
            const parsedValue = JSON.parse(fieldValue);
            fieldValue = parsedValue.label;
          }

          if (matchingField.fieldTypeId === CHECKBOX_ID) {
            fieldValue = matchingField.value === 'true' ? 'Yes' : 'No';
          }

          if (matchingField.fieldTypeId === CHECKBOX_GROUP_ID) {
            const parsedValue = JSON.parse(fieldValue!);
            fieldValue = parsedValue.map((v: any) => v.label).join(",");
          }

          if (matchingField.fieldTypeId === SELECT_LIST_ID) {
            const parsedValue = JSON.parse(fieldValue!);
            fieldValue = parsedValue.label;
          }

          if (matchingField.fieldTypeId === NOTES_ID) {
            subFieldValue = <>{matchingField.files && matchingField.files.map(f => {
              return <div key={f.fieldId}>{f.name}</div>
            })}</>
          }
        }

        return <div className={getclassNames("merge-option", isFieldSelected(p.id, `additionalInformation_${formField.name}`) ? 'highlight' : '')} key={p.id}>
          {getOption(`additionalInformation_${formField.name}`, p.id, formField.label, formField.fieldTypeId !== NOTES_ID, fieldValue)}
          {subFieldValue}
        </div>;
      })}
    </div>;
  };

  const getMergeField = (label: string, fieldName: string, onlyOneSelected: boolean) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          {getOption(fieldName, p.id, label, onlyOneSelected, (p as any)[fieldName])}
        </div>
      })}
    </div>
  }

  const getMergeDateField = (label: string, fieldName: string, onlyOneSelected: boolean) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        const currentValue = (p as any)[fieldName];
        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          {getOption(fieldName, p.id, label, onlyOneSelected, currentValue && formatJSONDateWithoutTime(currentValue, 'MM/dd/yyyy'))}
        </div>
      })}
    </div>
  }

  const getMergeSelectField = (label: string, fieldName: string, onlyOneSelected: boolean, propertyName: string, options: { label: string, value: string }[]) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        const currentValue = (p as any)[propertyName];
        const matchingOption = options.find(o => o.value === currentValue?.toString());
        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          {getOption(fieldName, p.id, label, onlyOneSelected, matchingOption?.label || '')}
        </div>
      })}
    </div>
  }

  const getMergeCheckboxGroupField = (label: string, fieldName: string, onlyOneSelected: boolean, propertyName: string, options: { label: string, value: string }[]) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        const currentValue = (p as any)[propertyName] as { id: number, name: string }[];
        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          {getOption(fieldName, p.id, label, onlyOneSelected, currentValue?.map(v => v.name)?.join(', ') || '')}
        </div>
      })}
    </div>
  }

  const getMergeRadioButtonField = (label: string, fieldName: string, onlyOneSelected: boolean, propertyName: string, options: { label: string, value: string }[]) => {
    return <div className="merge-option-row">
      {participants.map(p => {
        const currentValue = (p as any)[propertyName];
        const matchingOption = options.find(o => o.value === currentValue?.toString())?.label;
        return <div className={getclassNames("merge-option", isFieldSelected(p.id, fieldName) ? 'highlight' : '')} key={p.id}>
          {getOption(fieldName, p.id, label, onlyOneSelected, matchingOption || '')}
        </div>
      })}
    </div>
  }

  const getMergeFieldsTitle = () => {
    if (selectedClientId) {
      return <div className="merge-option-row">
        {participants.map((p, i) => {
          return <div className="merge-options-title" key={p.id}>
            {p.id === selectedClientId && <h2>Selected Participant: {p.firstName} {p.lastName}</h2>}
            {p.id !== selectedClientId && <h2>Participant {i}: {p.firstName} {p.lastName}</h2>}
          </div>
        })}
      </div>
    }

    return <div className="merge-option-row">
      {participants.map((p, i) => {
        return <div className="merge-options-title" key={p.id}>
          <h2>Participant {i + 1}: {p.firstName} {p.lastName}</h2>
        </div>
      })}
    </div>
  }

  const getMergedOptionsTitle = () => {
    return <div className="merge-options-title">
      <h2>Merged Participant</h2>
    </div>
  };

  const handleOnCancelMerge = () => {
    localStorage.removeItem(`mergeParticipants_${mergeId}`);
    navigate('/');
  };

  const handleOnConfirmMergeParticipants = () => {
    setConfirmMerge(true);
    setModalErrors(undefined);
    setErrors([]);
  };

  const handleOnCancelConfirmMergeParticipants = () => {
    setConfirmMerge(false);
    setModalErrors(undefined);
    setErrors([]);
  };

  const handleScrollLeft = () => {
    scrollLeft('.merge-options-parent-container');
  };

  const handleScrollRight = () => {
    scrollRight('.merge-options-parent-container');
  };

  if (loading) {
    return <Loader />
  }

  return (
    <div className="section">
      <section id="main-content" tabIndex={-1}>
        <h1>Merge Participants</h1>
        <div className="row justify-content-center">
          <div className="column-medium-11">
            <div className="row justify-content-between margin-vertical-3 align-items-center">
              <div className="column-auto">
                <ApiErrorMessages error={apiError} />
              </div>
            </div>
            <div className="row justify-content-center margin-vertical-5">
              <div className="column-auto">
                In each field, use the checkbox to select the data or files that you want to keep for the merged participant. Everything that is not selected will be <span className="text-bold">deleted</span> when you complete the merge.  
              </div>
            </div>
            <div className="row justify-content-between margin-vertical-3 align-items-center">
              <div className="column-auto">
                <BackButton />
              </div>
              <div className="column-auto">
                <div className="row align-items-center">
                  <div className="column-auto">
                    <IconButton id="scroll-left"
                      onClick={handleScrollLeft}
                      className="small flat"
                      icon={<i className="ri-arrow-left-s-line"></i>} />
                  </div>
                  <div className="column-auto">
                    <IconButton id="scroll-right"
                      onClick={handleScrollRight}
                      className="small flat"
                      icon={<i className="ri-arrow-right-s-line"></i>} />
                  </div>
                </div>
              </div>
              <div className="column-auto">
                <div className="row">
                  <div className="column-auto">
                    {submitting && <Loader />}
                  </div>
                  <div className="column-auto">
                    <Button id="merge-button"
                      text="Cancel Merge"
                      onClick={handleOnCancelMerge}
                      disabled={submitting}
                      className="action" />
                  </div>
                  <div className="column-auto">
                    <Button id="merge-button"
                      text="Merge Participants"
                      onClick={handleOnConfirmMergeParticipants}
                      disabled={submitting} />
                  </div>
                </div>
              </div>
            </div>
            <div className="merge-container">
              <div className="merge-options-parent-container">
                <div className="merge-options-containe0r">
                  <div>
                    {getMergeFieldsTitle()}
                    {getMergeField('First Name', 'firstName', true)}
                    {getMergeField('Middle Name', 'middleName', true)}
                    {getMergeField('Last Name', 'lastName', true)}
                    {getMergeDateField('Birth Date', 'dateOfBirth', true)}
                    {showPronouns && getMergeField('Preferred Pronouns', 'pronouns', true)}
                    {showPhone && getMergeField('Phone Number', 'phoneNumber', true)}
                    {showEmail && getMergeField('Email', 'email', true)}
                    {showGender && getMergeSelectField('Gender', 'gender', true, 'genderId', genders.map(g => ({ label: g.name, value: g.id.toString() })))}
                    {showRace && getMergeCheckboxGroupField('Race(s)', 'races', true, 'races', races.map(r => ({ label: r.name, value: r.id.toString() })))}
                    {showEthnicity && getMergeRadioButtonField('Ethnicity', 'ethnicity', true, 'ethnicityId', ethnicities.map(e => ({ label: e.name, value: e.id.toString() })))}
                    {showAddress && <>{getMergeField('Address Line 1', 'addressLine1', true)}{getMergeField('Address Line 2', 'addressLine2', true)}</>}
                    {showCity && getMergeField('Address Line City', 'addressCity', true)}
                    {showState && getMergeField('Address State', 'addressState', true)}
                    {showZip && getMergeField('Address Zip Code', 'addressZipCode', true)}
                    {additionalInformationForm?.fields && <h3>Additional Information</h3>}
                    {additionalInformationForm?.fields && additionalInformationForm.fields.filter(f => f.fieldTypeId !== CLIENT_ID &&
                      f.fieldTypeId !== BREAK_LINE_ID && f.fieldTypeId !== HEADER_ID
                    ).map(f => {
                      return getMergeFormField(f);
                    })}
                    <h3>Related Information</h3>
                    {getMergedParentField('Activated Log', 'activatedLog', false, 'activatedLog', false, (i: number, log: ClientActivatedLog) => {
                      return <div key={log.id}>
                        <div className="text-bold">Log {i + 1}</div>
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Activated:</span>
                          </div>
                          <div className="column-large-6">
                            {formatJSONDateWithoutTime(log.activated, 'MM/dd/yyyy')}
                          </div>
                        </div>
                        {log.deactivated &&
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Deactivated:</span>
                            </div>
                            <div className="column-large-6">
                              {formatJSONDateWithoutTime(log.deactivated, 'MM/dd/yyyy')}
                            </div>
                          </div>}
                        {!log.deactivated && <div className="row">
                          <div className="column text-italic">
                            Currently Active
                          </div>
                        </div>}
                      </div>
                    })}
                    {getMergedParentField('Household Members', 'householdMembers', false, 'householdMembers', false, (i: number, hhm: HouseholdMember) => {
                      return <div key={hhm.id}>
                        <div className="text-bold">Househouse Member {i + 1}</div>
                        {hhm.firstName} {hhm.lastName}
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Birth Date:</span>
                          </div>
                          <div className="column-large-6">
                            {hhm.dateOfBirth && formatJSONDateWithoutTime(hhm.dateOfBirth, 'MM/dd/yyyy')}
                          </div>
                        </div>
                        <div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Phone Number:</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.phoneNumber}
                            </div>
                          </div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Email:</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.email}
                            </div>
                          </div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Relationship:</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.relationshipName}
                            </div>
                          </div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Ethnicity:</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.ethnicityName}
                            </div>
                          </div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Race(s):</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.races && <TruncatedText value={hhm.races.map(r => r.name).join(',')}
                                id={`household-member-races-${hhm.id}`}
                                maxLength={10}
                                hideShowMore={true} />}
                            </div>
                          </div>
                          <div className="row">
                            <div className="column-large-6">
                              <span className="text-bold">Created:</span>
                            </div>
                            <div className="column-large-6">
                              {hhm.created && format(new Date(hhm.created), 'MM/dd/yyyy')}
                            </div>
                          </div>
                        </div>
                      </div>
                    })}
                    {getMergedParentField('Case Plans', 'casePlans', false, 'casePlans', false, (i: number, cp: ClientCasePlan) => {
                      return <div key={cp.id}>
                        <div className="text-bold">Case Plan {i + 1}</div>
                        <Link target="_blank" to={`/participants/${cp.clientId}/plans/${cp.id}`}>{cp.name}</Link>
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Status:</span>
                          </div>
                          <div className="column-large-6">
                            {cp.computedStatusName}
                          </div>
                        </div>
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Started:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(cp.started), 'MM/dd/yyyy')}
                          </div>
                        </div>
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Created:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(cp.created), 'MM/dd/yyyy')}
                          </div>
                        </div>
                      </div>
                    })}
                    {getMergedParentField('Submitted Forms', 'forms', false, 'forms', false, (i: number, f: FormInstance) => {
                      const activityDateField = f.instanceFields.find(inf => inf.fieldTypeId === ACTIVITY_DATE_ID);
                      let activityDate = '';
                      if (activityDateField && activityDateField.value) {
                        activityDate = activityDateField.value;
                      }
                      return <div key={f.id}>
                        <div className="text-bold">Form Submission {i + 1}</div>
                        <Link target="_blank" to={`/forms/${f.formId}/versions/${f.formVersionId}/instances/${f.id}/view`}>{f.formName}</Link>
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Name:</span>
                          </div>
                          <div className="column-large-6">
                            {f.formName}
                          </div>
                        </div>
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Activity Date:</span>
                          </div>
                          <div className="column-large-6">
                            {activityDate && format(new Date(activityDate), 'MM/dd/yyyy')}
                          </div>
                        </div>
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Created:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(f.created), 'MM/dd/yyyy')}
                          </div>
                        </div>
                      </div>
                    })}
                    {getMergedParentField('Program Enrollment', 'programs', false, 'programs', false, (i: number, pr: ProgramHistory) => {
                      return <div key={pr.programEnrollmentId}>
                        <div className="text-bold">Program Enrollment {i + 1}</div>
                        {pr.programName}
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Enrolled:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(pr.programStart), 'MM/dd/yyyy')}
                          </div>
                        </div>
                        {pr.programStop && <><div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Unenrolled:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(pr.programStop), 'MM/dd/yyyy')}
                          </div>
                        </div>
                          <div className="row">
                            <div className="column">
                              <span className="text-bold">Unenrollment Reason:</span>
                            </div>
                          </div>
                          <div className="row">
                            <div className="column">
                              <TruncatedText id={`${pr.programEnrollmentId}_stop_reason`}
                                value={pr.programStopReason || ''}
                                hideShowMore={true}
                                maxLength={15} />
                            </div>
                          </div>
                        </>}
                        {!pr.programStop && <div className="row">
                          <div className="column">
                            <span className="text-italic">Currently Enrolled</span>
                          </div>
                        </div>}

                      </div>
                    })}
                    {getMergedParentField('Profile Notes', 'notes', false, 'notes', false, (i: number, n: ClientNote) => {
                      return <div key={n.id}>
                        <div className="text-bold">AdHoc Note {i + 1}</div>
                        <TruncatedText id={`note_text_${n.id}`}
                          value={n.value}
                          maxLength={30}
                          hideShowMore={true} />
                        <br />
                        <Link to={`/participants/${n.clientId}/notes/${n.id}`}
                          target="_blank">Open Note</Link>
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Created By:</span>
                          </div>
                          <div className="column-large-6">
                            {n.createdByFirstName} {n.createdByLastName}
                          </div>
                        </div>
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Created:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(n.created), 'MM/dd/yyyy')}
                          </div>
                        </div>
                      </div>
                    })}
                    {getMergedParentField('Profile Files', 'files', false, 'files', false, (i: number, f: UploadedFile) => {
                      return <div key={f.fileId}>
                        <div className="text-bold">Profile File {i + 1}</div>
                        {f.name}
                        <br />
                        <Button id={`open-file`}
                          onClick={() => handleOnFileOpen(f.fileId)}
                          text="Open File"
                          className="action small"
                          loading={openingFiles.includes(f.fileId)}
                          disabled={openingFiles.includes(f.fileId)} />
                        <hr />
                        <div className="row">
                          <div className="column-large-6">
                            <span className="text-bold">Created:</span>
                          </div>
                          <div className="column-large-6">
                            {format(new Date(f.created), 'MM/dd/yyyy')}
                          </div>
                        </div>
                      </div>
                    })}
                  </div>
                </div>
              </div>
              <div className="merged-container">
                {getMergedOptionsTitle()}
                {getMergedOption('First Name', 'firstName', 'firstName', true)}
                {getMergedOption('Middle Name', 'middleName', 'middleName')}
                {getMergedOption('Last Name', 'lastName', 'lastName', true)}
                {getMergedDateOption('Birth Date', 'dateOfBirth', 'dateOfBirth', !optionalDateOfBirthEnabled)}
                {showPronouns && getMergedOption('Preferred Pronouns', 'pronouns', 'pronouns', true)}
                {showPhone && getMergedOption('Phone Number', 'phoneNumber', 'phoneNumber')}
                {showEmail && getMergedOption('Email', 'email', 'email')}
                {showGender && getMergedSelectOption('Gender', 'gender', 'genderId', genders.map(g => ({ label: g.name, value: g.id.toString() })))}
                {showRace && getMergedCheckboxGroupOption('Race(s)', 'races', 'races')}
                {showEthnicity && getMergedRadioButtonOption('Ethnicity', 'ethnicity', 'ethnicityId', ethnicities.map(e => ({ label: e.name, value: e.id.toString() })))}
                {showAddress && <>{getMergedOption('Address Line 1', 'addressLine1', 'addressLine1')}{getMergedOption('Address Line 2', 'addressLine2', 'addressLine2')}</>}
                {showCity && getMergedOption('Address Line City', 'addressCity', 'addressCity')}
                {showState && getMergedOption('Address State', 'addressState', 'addressState')}
                {showZip && getMergedOption('Address Zip Code', 'addressZipCode', 'addressZipCode')}
                {additionalInformationForm?.fields && <h3>Additional Information</h3>}
                {additionalInformationForm?.fields && additionalInformationForm.fields.filter(f => f.fieldTypeId !== CLIENT_ID &&
                  f.fieldTypeId !== BREAK_LINE_ID
                ).map(f => {
                  return getMergedFieldOption(f);
                })}
                <h3>Related Information</h3>
                {getMergedParentOption('Activated Log', 'activatedLog', 'activatedLog', (i: number, log: ClientActivatedLog) => {
                  return <div key={log.id}>
                    <div className="text-bold">Log {i + 1}</div>
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Activated:</span>
                      </div>
                      <div className="column-large-6">
                        {formatJSONDateWithoutTime(log.activated, 'MM/dd/yyyy')}
                      </div>
                    </div>
                    {log.deactivated &&
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Deactivated:</span>
                        </div>
                        <div className="column-large-6">
                          {formatJSONDateWithoutTime(log.deactivated, 'MM/dd/yyyy')}
                        </div>
                      </div>}
                    {!log.deactivated && <div className="row">
                      <div className="column text-italic">
                        Currently Active
                      </div>
                    </div>}
                  </div>
                }, true)}
                {getMergedParentOption('Household Members', 'householdMembers', 'householdMembers', (i: number, hhm: HouseholdMember) => {
                  return <div key={hhm.id}>
                    <div className="text-bold">Househouse Member {i + 1}</div>
                    {hhm.firstName} {hhm.lastName}
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Birth Date:</span>
                      </div>
                      <div className="column-large-6">
                        {hhm.dateOfBirth && formatJSONDateWithoutTime(hhm.dateOfBirth, 'MM/dd/yyyy')}
                      </div>
                    </div>
                    <div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Phone Number:</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.phoneNumber}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Email:</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.email}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Relationship:</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.relationshipName}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Ethnicity:</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.ethnicityName}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Race(s):</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.races && <TruncatedText value={hhm.races.map(r => r.name).join(',')}
                            id={`household-member-races-${hhm.id}`}
                            maxLength={10}
                            hideShowMore={true} />}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Created:</span>
                        </div>
                        <div className="column-large-6">
                          {hhm.created && format(new Date(hhm.created), 'MM/dd/yyyy')}
                        </div>
                      </div>
                    </div>
                  </div>
                })}
                {getMergedParentOption('Case Plans', 'casePlans', 'casePlans', (i: number, cp: ClientCasePlan) => {
                  return <div key={cp.id}>
                    <div className="text-bold">Case Plan {i + 1}</div>
                    <Link target="_blank" to={`/participants/${cp.clientId}/plans/${cp.id}`}>{cp.name}</Link>
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Status:</span>
                      </div>
                      <div className="column-large-6">
                        {cp.computedStatusName}
                      </div>
                    </div>
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Started:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(cp.started), 'MM/dd/yyyy')}
                      </div>
                    </div>
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Created:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(cp.created), 'MM/dd/yyyy')}
                      </div>
                    </div>
                  </div>
                })}
                {getMergedParentOption('Submitted Forms', 'forms', 'forms', (i: number, f: FormInstance) => {
                  const activityDateField = f.instanceFields.find(inf => inf.fieldTypeId === ACTIVITY_DATE_ID);
                  let activityDate = '';
                  if (activityDateField && activityDateField.value) {
                    activityDate = activityDateField.value;
                  }
                  return <div key={f.id}>
                    <div className="text-bold">Form Submission {i + 1}</div>
                    <Link target="_blank" to={`/forms/${f.formId}/versions/${f.formVersionId}/instances/${f.id}/view`}>{f.formName}</Link>
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Name:</span>
                      </div>
                      <div className="column-large-6">
                        {f.formName}
                      </div>
                    </div>
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Activity Date:</span>
                      </div>
                      <div className="column-large-6">
                        {activityDate && format(new Date(activityDate), 'MM/dd/yyyy')}
                      </div>
                    </div>
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Created:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(f.created), 'MM/dd/yyyy')}
                      </div>
                    </div>
                  </div>
                })}
                {getMergedParentOption('Program Enrollment', 'programs', 'programs', (i: number, pr: ProgramHistory) => {
                  return <div key={pr.programEnrollmentId}>
                    <div className="text-bold">Program Enrollment {i + 1}</div>
                    {pr.programName}
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Enrolled:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(pr.programStart), 'MM/dd/yyyy')}
                      </div>
                    </div>
                    {!pr.programStop && <div className="row">
                      <div className="column text-italic">
                        Currently Enrolled
                      </div>
                    </div>}
                    {pr.programStop && <>
                      <div className="row">
                        <div className="column-large-6">
                          <span className="text-bold">Unenrolled:</span>
                        </div>
                        <div className="column-large-6">
                          {format(new Date(pr.programStop), 'MM/dd/yyyy')}
                        </div>
                      </div>
                      <div className="row">
                        <div className="column">
                          <span className="text-bold">Unenrollment Reason:</span>
                        </div>
                      </div>
                      <div className="row">
                        <div className="column">
                          <TruncatedText id={`${pr.programEnrollmentId}_stop_reason`}
                            value={pr.programStopReason || ''}
                            hideShowMore={true}
                            maxLength={15} />
                        </div>
                      </div>
                    </>}
                  </div>
                })}
                {getMergedParentOption('Profile Notes', 'notes', 'notes', (i: number, n: ClientNote) => {
                  return <div key={n.id}>
                    <div className="text-bold">AdHoc Note {i + 1}</div>
                    <TruncatedText id={`note_text_${n.id}`}
                      value={n.value}
                      maxLength={30}
                      hideShowMore={true} />
                    <br />
                    <Link to={`/participants/${n.clientId}/notes/${n.id}`}
                      target="_blank">Open Note</Link>
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Created By:</span>
                      </div>
                      <div className="column-large-6">
                        {n.createdByFirstName} {n.createdByLastName}
                      </div>
                    </div>
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Created:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(n.created), 'MM/dd/yyyy')}
                      </div>
                    </div>
                  </div>
                })}
                {getMergedParentOption('Profile Files', 'files', 'files', (i: number, f: UploadedFile) => {
                  return <div key={f.fileId}>
                    <div className="text-bold">Profile File {i + 1}</div>
                    {f.name}
                    <br />
                    <Button id={`open-file`}
                      onClick={() => handleOnFileOpen(f.fileId)}
                      text="Open File"
                      className="action small"
                      loading={openingFiles.includes(f.fileId)}
                      disabled={openingFiles.includes(f.fileId)} />
                    <hr />
                    <div className="row">
                      <div className="column-large-6">
                        <span className="text-bold">Created:</span>
                      </div>
                      <div className="column-large-6">
                        {format(new Date(f.created), 'MM/dd/yyyy')}
                      </div>
                    </div>
                  </div>
                })}
              </div>
            </div>


          </div>
        </div>

      </section>
      <Modal id="merge-confirm-modal" visible={confirmMerge} className="small" title="Confirm Merge Participants" onClose={handleOnCancelConfirmMergeParticipants}>
        <>
          <ApiErrorMessages error={modalErrors} />
          <ErrorMessages messages={errors} />
          <div className="row">
            <div className="column">
              <strong>Are you sure you want to merge these participants?</strong>
              <p>Only the selected information will be retained - everything else will be permanently deleted.</p>
              <p>This action cannot be undone.</p>
            </div>
          </div>
          <div className="row margin-top-5">
            <div className="column">
              <div className="row justify-content-between">
                <div className="column-medium-auto">
                  <Button id="cancel-merge"
                    text="Cancel"
                    className="secondary"
                    disabled={submitting}
                    onClick={handleOnCancelConfirmMergeParticipants} />
                </div>
                <div className="column-medium-auto">
                  <Button id="confirm-merge"
                    text="Merge"
                    className="warning"
                    disabled={submitting}
                    loading={submitting}
                    onClick={handleOnMerge} />
                </div>
              </div>
            </div>
          </div>
        </>
      </Modal>
    </div>
  );
};

export default ParticipantMergeCompare;
